/**
 * Caption Writing-Tools — the pure instruction strings + platform mapping
 * behind PostBody's caption AI block (Phase 1 of the post-edit-flow spec,
 * docs/superpowers/specs/2026-06-30-post-edit-flow-design.md §5.1).
 *
 * The 3-verb text-action row + Tone popover + the freeform "Describe a change…"
 * field ALL feed the SAME `rewriteCaption({ instruction })` call. The chip
 * is "a preset you typed": it just supplies the instruction string. Keeping
 * these strings here (not inline JSX) makes them unit-testable and keeps the
 * 1,800-line PostBody free of copy that drifts.
 */
import type { RewritePlatform } from '@/services/media/caption-rewrite';

export type CaptionVerb = 'shorten' | 'punchier' | 'add-hook';
export type CaptionTone = 'friendly' | 'professional' | 'casual';

export const CAPTION_VERBS: ReadonlyArray<{ id: CaptionVerb; label: string }> = [
  { id: 'shorten', label: 'Shorten' },
  { id: 'punchier', label: 'Punchier' },
  { id: 'add-hook', label: 'Add hook' },
];

export const CAPTION_TONES: ReadonlyArray<{ id: CaptionTone; label: string }> = [
  { id: 'friendly', label: 'Friendly' },
  { id: 'professional', label: 'Professional' },
  { id: 'casual', label: 'Casual' },
];

const VERB_INSTRUCTIONS: Record<CaptionVerb, string> = {
  shorten: 'Trim this caption to 1-2 punchy sentences while keeping the hook and the core message.',
  punchier:
    'Make this caption punchier — stronger verbs, tighter phrasing, more energy. Keep the same meaning and length.',
  'add-hook':
    'Add a scroll-stopping opening hook to the first line of this caption. Keep the rest of the message intact.',
};

const TONE_INSTRUCTIONS: Record<CaptionTone, string> = {
  friendly: 'Rewrite this caption in a warm, friendly, approachable voice. Keep the same meaning.',
  professional:
    'Rewrite this caption in a polished, professional voice. Keep the same meaning and key details.',
  casual: 'Rewrite this caption in a relaxed, conversational, casual voice. Keep the same meaning.',
};

export function captionVerbInstruction(verb: CaptionVerb): string {
  return VERB_INSTRUCTIONS[verb];
}

export function captionToneInstruction(tone: CaptionTone): string {
  return TONE_INSTRUCTIONS[tone];
}

/**
 * The instruction behind the platform-specific tab's "Rewrite for X" pill.
 *
 * Takes the DISPLAY label ("LinkedIn"), never the raw platform id — the id is
 * already carried machine-readably by the `platform` field on the request, and
 * a raw id in the prose ("rewrite for x") reads as a bug to the model and to
 * anyone reading the prompt log.
 *
 * Deliberately contains no character count: the route injects
 * getPlatformSpec(platform).caption.maxChars into the system prompt, and a
 * number here would be a second source of truth for the same constraint.
 */
export function captionPlatformRewriteInstruction(platformLabel: string): string {
  return `Rewrite this caption specifically for ${platformLabel}. Match that platform's conventions — tone, structure, hook placement, hashtag and emoji norms — and keep the core message and any concrete details such as offers, dates and product names intact.`;
}

/**
 * Map a Mora `PlatformId` to the `/api/content/rewrite` platform enum.
 * The endpoint speaks `twitter`, our registry speaks `x` — bridge it here.
 * Returns `undefined` for any platform the rewrite endpoint does not accept,
 * so the caller simply omits `platform` (the endpoint treats it as generic).
 * The endpoint accepts all eight Mora platforms: instagram, tiktok, twitter,
 * linkedin, facebook, threads, pinterest, and youtube.
 */
export function toRewritePlatform(
  platform: string | null | undefined,
): RewritePlatform | undefined {
  if (!platform) return undefined;
  if (platform === 'x' || platform === 'twitter') return 'twitter';
  if (
    platform === 'instagram' ||
    platform === 'tiktok' ||
    platform === 'linkedin' ||
    platform === 'facebook' ||
    platform === 'threads' ||
    platform === 'pinterest' ||
    platform === 'youtube'
  ) {
    return platform as RewritePlatform;
  }
  return undefined;
}
